import React, { useState, useEffect } from "react";
import { Bell, X, CheckCircle } from "lucide-react";
import { Button } from "../ui/button";
import { usePushNotifications } from "../../lib/usePushNotifications";
import { useAuth } from "../../contexts";

export const PushNotificationPrompt = () => {
  const { user, token } = useAuth();
  const [showPrompt, setShowPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  
  const {
    isSupported,
    isSubscribed,
    permission,
    subscribe,
    requestPermission
  } = usePushNotifications(token);

  useEffect(() => {
    // Show prompt if:
    // 1. User is logged in
    // 2. Push is supported
    // 3. Not already subscribed
    // 4. Permission not denied
    // 5. User hasn't dismissed it this session
    const hasSeenPrompt = sessionStorage.getItem('push-prompt-dismissed');
    
    if (user && isSupported && !isSubscribed && permission !== 'denied' && !hasSeenPrompt) {
      // Delay showing prompt for better UX
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [user, isSupported, isSubscribed, permission]);

  const handleEnable = async () => {
    const success = await subscribe();
    if (success) {
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowPrompt(false);
    sessionStorage.setItem('push-prompt-dismissed', 'true');
  };

  if (!showPrompt || dismissed) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-4">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-4 max-w-sm">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-[#FF6B00] rounded-full flex items-center justify-center flex-shrink-0">
            <Bell className="text-white" size={20} />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-[#002147] mb-1">Enable Notifications</h3>
            <p className="text-sm text-gray-600 mb-3">
              Get instant updates about grades, assignments, and school announcements.
            </p>
            <div className="flex gap-2">
              <Button 
                size="sm" 
                className="bg-[#FF6B00] hover:bg-[#E65C00]"
                onClick={handleEnable}
                data-testid="enable-notifications-btn"
              >
                <CheckCircle size={14} className="mr-1" /> Enable
              </Button>
              <Button 
                size="sm" 
                variant="ghost"
                onClick={handleDismiss}
              >
                Not now
              </Button>
            </div>
          </div>
          <button 
            onClick={handleDismiss}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PushNotificationPrompt;
